export { SecureChannel } from "./channel.js";
export type {
  SecureChannelConfig,
  ChannelState,
  MessageMetadata,
  AttachmentData,
  PersistedState,
  LegacyPersistedState,
  DeviceSession,
  HistoryEntry,
  SendOptions,
  DecisionOption,
  DecisionRequest,
  DecisionResponse,
  ContextRef,
  HeartbeatStatus,
  StatusAlert,
  RoomInfo,
  RoomMemberInfo,
  RoomConversationInfo,
  RoomState,
  A2AChannel,
  A2AMessage,
  RoomParticipantEvent,
  DeliveryTarget,
  DeliveryContent,
  DeliveryOptions,
  DeliveryReceipt,
  TargetInfo,
  ActionConfirmation,
  ArtifactPayload,
  PolicyAlert,
  ApprovalRequest,
  ApprovalResponse,
  CredentialGrantPayload,
  CredentialRevokePayload,
  CredentialAckPayload,
  CredentialRequestPayload,
} from "./types.js";
export { parseTarget } from "./types.js";

// Host-local agent history recovery code management.
export {
  historyRecoveryCodePath,
  loadHistoryRecoveryCode,
  configureHistoryRecoveryCode,
  clearHistoryRecoveryCode,
  readMaskedHistoryRecoveryCode,
  runHistoryRecoveryCommand,
  offerHistoryRecoveryConfiguration,
} from "./history-recovery-code.js";
export type {
  StoredHistoryRecoveryCode,
  HistoryRecoveryCommandInput,
} from "./history-recovery-code.js";

// Credential store (in-memory, never persisted)
export { CredentialStore } from "./credential-store.js";
export type { RenterCredential, CredentialInfo } from "./credential-store.js";

// Multi-account config resolution
export { listAccountIds, resolveAccount } from "./account-config.js";
export type { ResolvedAccount } from "./account-config.js";

// OpenClaw channel plugin
export { agentVaultPlugin, setOcRuntime, getActiveChannel } from "./openclaw-plugin.js";

// Gateway send helpers
export { sendToOwner, sendToRoom, sendToTarget, listTargets, sendDecisionToOwner, checkGateway } from "./gateway-send.js";
export type {
  GatewaySendOptions,
  GatewaySendResult,
  GatewayStatusResult,
} from "./gateway-send.js";

// OpenClaw SDK compat helpers
export { requestHeartbeatNow, onAgentEvent, onSessionTranscriptUpdate } from "./openclaw-compat.js";
// #1042 — `managedRoutesRequested` says what the plugin DID (made the six registerHttpRoute
// calls); OpenClaw never tells the plugin whether they were accepted. The old name is kept
// as a deprecated live-binding alias for any external importer.
export { managedRoutesRequested, managedRoutesRequested as isUsingManagedRoutes } from "./openclaw-entry.js";

// HTTP handler functions (shared between self-managed server and managed routes)
export { handleSendRequest, handleActionRequest, handleDecisionRequest, handleStatusRequest, handleTargetsRequest } from "./http-handlers.js";
export type { HandlerResult } from "./http-handlers.js";

// MCP server (Streamable HTTP transport for skill invocation)
export { AgentVaultMcpServer } from "./mcp-server.js";
export type { SkillDefinition, McpServerOpts } from "./mcp-server.js";

// MCP HTTP handlers (route /mcp requests to MCP server)
export { createMcpHandler, getMcpStatus } from "./mcp-handlers.js";

// Skill manifest loader (SKILL.md + backend API)
export { parseSkillMd, loadSkillsFromDirectory, loadSkillsFromApi, mergeSkills } from "./skill-manifest.js";
export type { SkillManifest } from "./skill-manifest.js";

// Skill invocation lifecycle
export { invokeSkill } from "./skill-invoker.js";
export type { InvocationResult, InvocationOpts } from "./skill-invoker.js";

// Skill telemetry emission
export { wrapSkillExecution, reportSkillInvocation, drainSkillSpans } from "./skill-telemetry.js";

// Policy enforcement
export { PolicyEnforcer } from "./policy-enforcer.js";
export type { PolicyContext, PolicyResult, PolicyViolation, PolicyMetrics } from "./policy-enforcer.js";

// OpenClaw SDK types (re-exports with fallbacks)
export type {
  OpenClawPluginApi,
  PluginRuntime,
  ChannelGatewayContext,
  ChannelOutboundPayloadContext,
  ReplyPayload,
  MessageSentEvent,
  SessionStartEvent,
  SessionEndEvent,
  AgentEventPayload,
  TranscriptUpdatePayload,
} from "./openclaw-types.js";

// Read version from package.json at build time (injected by esbuild define)
// Fallback to require for non-bundled contexts (tests)
export const VERSION: string = typeof __AV_VERSION__ !== "undefined"
  ? __AV_VERSION__
  : "0.0.0-dev";

// #1107 — the room conversation protocol. Exported so every TypeScript client
// (the plugin AND the three native bridges) decides replies with ONE rule.
export { owesRoomReply, parseMentions, stripMentions, stripCaptionMentions } from "./room-protocol.js";
